import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    getConversationNotes, 
    createNote, 
    updateNote, 
    deleteNote, 
    markAllNotesAsRead 
} from '../../Requests/ConversationRequests';
import { PlusIcon, PaperAirplaneIcon, CheckIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import UpgradedLoader from '../UpgradedLoader';
import ConfirmationModal from '../ConfirmationModal';

function ConversationDetail({ conversation, onConversationUpdate }) {
    const { t } = useTranslation();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newNoteContent, setNewNoteContent] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [editNoteContent, setEditNoteContent] = useState('');
    const [deleteNoteModalOpen, setDeleteNoteModalOpen] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [deletingNote, setDeletingNote] = useState(false);
    const notesEndRef = useRef(null);
    const textareaRef = useRef(null);

    // Load conversation notes
    useEffect(() => {
        if (conversation?.uuid) {
            setLoading(true);
            getConversationNotes(conversation.uuid)
                .then((response) => {
                    const notesData = response.data || [];
                    setNotes(notesData);
                    
                    // Mark all notes as read when conversation is opened
                    if (notesData.some(note => !note.is_read)) {
                        markAllNotesAsRead(conversation.uuid).catch(console.error);
                        
                        // Update conversation with 0 unread count
                        if (onConversationUpdate) {
                            onConversationUpdate({
                                ...conversation,
                                unread_count: 0
                            });
                        }
                    }
                })
                .catch((error) => {
                    console.error('Error loading conversation notes:', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [conversation?.uuid, onConversationUpdate]);

    // Scroll to bottom when notes change
    useEffect(() => {
        scrollToBottom();
    }, [notes]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [newNoteContent]);

    const scrollToBottom = () => {
        notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Handle adding new note
    const handleAddNote = async (e) => {
        e.preventDefault();
        if (!newNoteContent.trim()) return;

        setAddingNote(true);
        try {
            const noteData = {
                content: newNoteContent.trim()
            };

            const response = await createNote(conversation.uuid, noteData);
            const newNote = response.data;

            setNotes([...notes, newNote]);
            setNewNoteContent('');
            
            // Update conversation notes count
            if (onConversationUpdate) {
                onConversationUpdate({
                    ...conversation,
                    notes_count: (conversation.notes_count || 0) + 1
                });
            }
        } catch (error) {
            console.error('Error adding note:', error);
        } finally {
            setAddingNote(false);
        }
    };

    // Handle editing note
    const handleEditNote = async (noteUuid) => {
        if (!editNoteContent.trim()) return;

        try {
            const noteData = {
                content: editNoteContent.trim()
            };

            const response = await updateNote(noteUuid, noteData);
            const updatedNote = response.data;

            setNotes(notes.map(note => 
                note.uuid === noteUuid ? updatedNote : note
            ));
            setEditingNote(null);
            setEditNoteContent('');
        } catch (error) {
            console.error('Error updating note:', error);
        }
    };

    // Handle deleting note
    const handleDeleteNote = (note) => {
        setNoteToDelete(note);
        setDeleteNoteModalOpen(true);
    };

    const confirmDeleteNote = async () => {
        if (!noteToDelete) return;

        try {
            setDeletingNote(true);
            await deleteNote(noteToDelete.uuid);
            const updatedNotes = notes.filter(note => note.uuid !== noteToDelete.uuid);
            setNotes(updatedNotes);
            
            // Update conversation notes count
            if (onConversationUpdate) {
                onConversationUpdate({
                    ...conversation,
                    notes_count: Math.max(0, (conversation.notes_count || 0) - 1)
                });
            }

            setDeleteNoteModalOpen(false);
            setNoteToDelete(null);
        } catch (error) {
            console.error('Error deleting note:', error);
        } finally {
            setDeletingNote(false);
        }
    };

    // Format time ago
    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMins < 1) return t('Conversations.justNow');
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString();
    };

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <UpgradedLoader className="animate-spin" w={6} h={6} bg="bg-iavisus-100" />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
                <h3 className="text-lg font-medium text-gray-900">{conversation.title}</h3>
                {conversation.description && (
                    <p className="mt-1 text-sm text-gray-500">{conversation.description}</p>
                )}
                <div className="mt-2 flex items-center justify-between">
                    <p className="text-xs text-gray-400">
                        {notes.length} {notes.length === 1 ? 'note' : 'notes'}
                    </p>
                    {notes.some(note => !note.is_read) && (
                        <button
                            onClick={() => markAllNotesAsRead(conversation.uuid)}
                            className="text-xs text-iavisus-100 hover:text-iavisus-200 flex items-center"
                        >
                            <CheckIcon className="h-3 w-3 mr-1" />
                            Mark all as read
                        </button>
                    )}
                </div>
            </div>

            {/* Notes */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {notes.length === 0 ? (
                    <div className="text-center py-12">
                        <PlusIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('Notes.noNotes')}</h3>
                        <p className="mt-1 text-sm text-gray-500">{t('Notes.startDiscussion')}</p>
                    </div>
                ) : (
                    notes.map((note) => (
                        <div key={note.uuid} className={`flex ${note.user?.uuid === 'current-user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                note.user?.uuid === 'current-user'
                                    ? 'bg-iavisus-100 text-white'
                                    : 'bg-gray-200 text-gray-900'
                            }`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        {editingNote === note.uuid ? (
                                            <div className="space-y-2">
                                                <textarea
                                                    value={editNoteContent}
                                                    onChange={(e) => setEditNoteContent(e.target.value)}
                                                    className="w-full p-2 text-sm border rounded resize-none text-gray-900 bg-white"
                                                    rows="3"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            handleEditNote(note.uuid);
                                                        }
                                                        if (e.key === 'Escape') {
                                                            setEditingNote(null);
                                                            setEditNoteContent('');
                                                        }
                                                    }}
                                                />
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleEditNote(note.uuid)}
                                                        className="px-2 py-1 text-xs bg-white text-iavisus-100 rounded hover:bg-gray-100"
                                                    >
                                                        {t('Notes.saveNote')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEditingNote(null);
                                                            setEditNoteContent('');
                                                        }}
                                                        className="px-2 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                                                    >
                                                        {t('Notes.cancelEdit')}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-sm whitespace-pre-wrap break-words">{note.content}</p>
                                                <div className="mt-2 flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        <p className={`text-xs ${
                                                            note.user?.uuid === 'current-user' ? 'text-white opacity-75' : 'text-gray-500'
                                                        }`}>
                                                            {note.user?.name || 'Unknown User'}
                                                        </p>
                                                        <span className={`text-xs ${
                                                            note.user?.uuid === 'current-user' ? 'text-white opacity-50' : 'text-gray-400'
                                                        }`}>
                                                            •
                                                        </span>
                                                        <p className={`text-xs ${
                                                            note.user?.uuid === 'current-user' ? 'text-white opacity-75' : 'text-gray-500'
                                                        }`}>
                                                            {formatTimeAgo(note.created_at)}
                                                        </p>
                                                    </div>
                                                    
                                                    {/* Note actions menu */}
                                                    {note.user?.uuid === 'current-user' && (
                                                        <Menu as="div" className="relative">
                                                            <MenuButton className={`p-1 rounded-full hover:bg-black hover:bg-opacity-10 ${
                                                                note.user?.uuid === 'current-user' ? 'text-white' : 'text-gray-500'
                                                            }`}>
                                                                <EllipsisVerticalIcon className="h-3 w-3" />
                                                            </MenuButton>
                                                            <MenuItems className="absolute right-0 z-10 mt-1 w-32 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                                <MenuItem>
                                                                    <button
                                                                        onClick={() => {
                                                                            setEditingNote(note.uuid);
                                                                            setEditNoteContent(note.content);
                                                                        }}
                                                                        className="flex w-full items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                                    >
                                                                        <PencilIcon className="mr-2 h-4 w-4" />
                                                                        {t('Notes.editNote')}
                                                                    </button>
                                                                </MenuItem>
                                                                <MenuItem>
                                                                    <button
                                                                        onClick={() => handleDeleteNote(note)}
                                                                        className="flex w-full items-center px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                                                                    >
                                                                        <TrashIcon className="mr-2 h-4 w-4" />
                                                                        {t('Notes.deleteNote')}
                                                                    </button>
                                                                </MenuItem>
                                                            </MenuItems>
                                                        </Menu>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Read status indicator */}
                                {!note.is_read && note.user?.uuid !== 'current-user' && (
                                    <div className="mt-1 flex justify-end">
                                        <div className="h-2 w-2 bg-iavisus-100 rounded-full"></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
                <div ref={notesEndRef} />
            </div>

            {/* Add Note Form */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                <form onSubmit={handleAddNote} className="flex space-x-3">
                    <div className="flex-1">
                        <textarea
                            ref={textareaRef}
                            value={newNoteContent}
                            onChange={(e) => setNewNoteContent(e.target.value)}
                            placeholder={t('Notes.addNotePlaceholder')}
                            className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-iavisus-100 sm:text-sm resize-none min-h-[40px] max-h-32 overflow-y-auto"
                            rows="1"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleAddNote(e);
                                }
                            }}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={addingNote || !newNoteContent.trim()}
                        className="inline-flex items-center rounded-full bg-iavisus-100 p-2 text-white shadow-sm hover:bg-iavisus-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-iavisus-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {addingNote ? (
                            <UpgradedLoader className="animate-spin" w={4} h={4} bg="bg-white" />
                        ) : (
                            <PaperAirplaneIcon className="h-4 w-4" />
                        )}
                    </button>
                </form>
            </div>

            {/* Delete Note Confirmation Modal */}
            <ConfirmationModal
                open={deleteNoteModalOpen}
                setOpen={setDeleteNoteModalOpen}
                title={t('Notes.deleteNoteTitle')}
                message={t('Notes.deleteNoteMessage')}
                onConfirm={confirmDeleteNote}
                loading={deletingNote}
                variant="danger"
                confirmText={t('Notes.deleteNote')}
                cancelText={t('common.cancel')}
            />
        </div>
    );
}

export default ConversationDetail;